import { and, asc, eq } from "drizzle-orm";
import { combo, combo_item, type PowerSyncDrizzleDB, product_variant } from "@comandero/powersync";
import { liveComboFilters, liveComboItemFilters, liveProductVariantFilters } from "../live/filters";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

// ─── Variantes de producto (tamaños / opciones con precio propio) ─────────────

/** Variantes de un producto (S/M/L), ordenadas. `price` es ABSOLUTO en centavos. */
export function getVariantsQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db
    .select()
    .from(product_variant)
    .where(and(eq(product_variant.product_id, productId), ...liveProductVariantFilters()))
    .orderBy(asc(product_variant.sort_order), asc(product_variant.name));
}

/** Crea una variante con precio absoluto (centavos). CC-7: escritura con await. */
export async function createVariantMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  productId: string,
  name: string,
  price: number,
  opts?: { sortOrder?: number },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(product_variant).values({
    id,
    organization_id: organizationId,
    product_id: productId,
    name: name.trim(),
    price: Math.trunc(price),
    sort_order: opts?.sortOrder ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteVariantMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const now = nowISO();
  await db
    .update(product_variant)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(product_variant.id, id));
}

// ─── Combos / menús combinados a precio especial ──────────────────────────────

export function getCombosQuery(db: PowerSyncDrizzleDB, organizationId: string) {
  return db
    .select()
    .from(combo)
    .where(and(eq(combo.organization_id, organizationId), ...liveComboFilters()))
    .orderBy(asc(combo.name));
}

export function getComboItemsQuery(db: PowerSyncDrizzleDB, comboId: string) {
  return db
    .select()
    .from(combo_item)
    .where(and(eq(combo_item.combo_id, comboId), ...liveComboItemFilters()));
}

/** Crea un combo a precio fijo (centavos). CC-7: escritura con await. */
export async function createComboMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  price: number,
  opts?: { taxRate?: number },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(combo).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    price: Math.trunc(price),
    tax_rate: opts?.taxRate ?? 0,
    is_available: 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteComboMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const now = nowISO();
  await db.update(combo).set({ deleted_at: now, updated_at: now }).where(eq(combo.id, id));
}

/** Agrega un producto a un combo (con cantidad). */
export async function addComboItemMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  comboId: string,
  productId: string,
  qty = 1,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(combo_item).values({
    id,
    organization_id: organizationId,
    combo_id: comboId,
    product_id: productId,
    qty: Math.max(1, Math.trunc(qty)),
    created_at: now,
    updated_at: now,
  });
  return id;
}
